import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload } from "lucide-react";
import { api, formatRupiah, imageUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCategories } from "@/hooks/useCategories";

const EMPTY = { name: "", description: "", category: "", price: "", stock: "", image_url: "" };

export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { categories, labels } = useCategories();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api
      .get("/products/all")
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Gagal memuat produk"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, category: categories[0]?.key || "" });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description,
      category: p.category,
      price: String(p.price),
      stock: String(p.stock),
      image_url: p.image_url,
    });
    setOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Foto produk terunggah");
    } catch {
      toast.error("Gagal mengunggah foto");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseInt(form.price, 10) || 0,
      stock: parseInt(form.stock, 10) || 0,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Produk diperbarui");
      } else {
        await api.post("/products", payload);
        toast.success("Produk baru ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal menyimpan produk");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produk dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus produk");
    }
  };

  const inputCls =
    "w-full px-4 py-2.5 rounded-xl border border-[#D5CCBC] bg-white text-sm text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]";

  return (
    <div data-testid="products-admin">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-[#1E2B1D]">
            Manajemen Stok Produk
          </h1>
          <p className="text-xs text-[#6B7C66] mt-1">
            Tambah, ubah, dan atur stok rangkaian bunga yang tampil di etalase toko.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-product-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] text-[#FAF7EE] text-sm font-bold transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" /> Tambah Produk
        </button>
      </div>

      <div className="bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E8E2D5] text-left text-xs uppercase tracking-wider text-[#7E8F7A]">
              <th className="p-4">Produk</th>
              <th className="p-4">Kategori</th>
              <th className="p-4">Harga</th>
              <th className="p-4">Stok</th>
              <th className="p-4 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-[#6B7C66]" data-testid="products-loading">
                  Memuat produk...
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-[#6B7C66]" data-testid="products-empty">
                  Belum ada produk. Klik "Tambah Produk" untuk mengisi stok.
                </td>
              </tr>
            ) : (
              products.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`product-row-${p.id}`}
                  className="border-b border-[#E8E2D5] last:border-0"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={imageUrl(p.image_url)}
                        alt={p.name}
                        className="w-12 h-12 rounded-lg object-cover bg-[#F4EFE6]"
                      />
                      <div>
                        <p className="font-bold text-[#1E2B1D]">{p.name}</p>
                        <p className="text-xs text-[#7E8F7A] line-clamp-1 max-w-xs">{p.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-xs text-[#4A5746]">
                    {labels[p.category] || p.category}
                  </td>
                  <td className="p-4 font-semibold text-[#203120]">{formatRupiah(p.price)}</td>
                  <td className="p-4">
                    <span
                      data-testid={`product-stock-badge-${p.id}`}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        p.stock > 0 ? "bg-[#EAF2E9] text-[#3D5A40]" : "bg-red-100 text-red-700"
                      }`}
                    >
                      {p.stock}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`edit-product-${p.id}`}
                        className="p-2 rounded-lg border border-[#D5CCBC] text-[#3D5A40] hover:bg-[#F4EFE6] transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(p)}
                        data-testid={`delete-product-${p.id}`}
                        className="p-2 rounded-lg border border-[#D5CCBC] text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FBF9F5] border border-[#DACFBE] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl text-[#1E2B1D]">
              {editing ? "Ubah Produk" : "Tambah Produk Baru"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="product-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5">
                Nama Produk
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="product-name-input"
                placeholder="Contoh: Buket Mawar Putih Premium"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5">
                Deskripsi
              </label>
              <textarea
                rows="2"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="product-description-input"
                placeholder="Jelaskan isi rangkaian, ukuran, dan keistimewaannya"
                className={inputCls}
              ></textarea>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5">
                  Kategori
                </label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
                >
                  <SelectTrigger data-testid="product-category-select" className="border-[#D5CCBC] bg-white rounded-xl">
                    <SelectValue placeholder="Pilih kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.key} value={c.key} data-testid={`category-option-${c.key}`}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5">
                  Stok
                </label>
                <input
                  required
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))}
                  data-testid="product-stock-input"
                  placeholder="0"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5">
                Harga (Rp)
              </label>
              <input
                required
                type="number"
                min="0"
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                data-testid="product-price-input"
                placeholder="450000"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-1.5">
                Foto Produk
              </label>
              <div className="flex items-center gap-3">
                {form.image_url ? (
                  <img
                    src={imageUrl(form.image_url)}
                    alt="Foto produk"
                    data-testid="product-image-preview"
                    className="w-16 h-16 rounded-xl object-cover border border-[#E8E2D5]"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-[#F4EFE6] border border-dashed border-[#C5BCAB] flex items-center justify-center text-[10px] text-[#7E8F7A]">
                    Kosong
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={uploadImage}
                  data-testid="product-image-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="product-image-upload-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#3D5A40] text-[#3D5A40] text-xs font-bold hover:bg-[#3D5A40] hover:text-[#FBF9F5] disabled:opacity-50 transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? "Mengunggah..." : "Unggah Foto"}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="product-save-btn"
              className="w-full py-3 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] disabled:opacity-50 text-[#FAF7EE] font-bold text-sm transition-colors"
            >
              {saving ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Tambah ke Etalase"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
